import { useMemo, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Delete02Icon,
  Edit02Icon,
  PlusSignIcon,
} from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePermissionList, useTogglePermission } from "../hooks/usePermissions";
import { usePermissionStore } from "../stores/permissionStore";
import type { Permission } from "../types/permission.types";
import { groupByPermissionGroup } from "../utils/groupByPermissionGroup";

export function PermissionList() {
  const { data, isLoading } = usePermissionList();
  const openAdd = usePermissionStore((s) => s.openDialogForAdd);
  const openEdit = usePermissionStore((s) => s.openDialogForEdit);
  const toggleMutation = useTogglePermission();
  const [pendingDelete, setPendingDelete] = useState<Permission | null>(null);

  const grouped = useMemo(() => groupByPermissionGroup(data), [data]);

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-3 py-2">
        <div>
          <h2 className="text-sm font-bold text-gray-900">권한 카탈로그</h2>
          <p className="text-xs text-gray-500">
            시스템 전역 권한 — 전체 회사에 즉시 반영
          </p>
        </div>
        <Button
          size="sm"
          onClick={openAdd}
          className="bg-blue-600 px-3 py-1.5 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={12} className="mr-1" />
          권한 추가
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            조회 중...
          </div>
        ) : grouped.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            등록된 권한이 없습니다
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {grouped.map(({ group, items }) => (
              <div key={group}>
                <div className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-3 py-1.5">
                  <span className="text-xs font-medium uppercase text-gray-500">
                    {group}
                  </span>
                  <span className="ml-2 text-xs text-gray-400">
                    {items.length}
                  </span>
                </div>
                <ul>
                  {items.map((p) => (
                    <li
                      key={p.guid}
                      className="group flex items-start justify-between gap-2 border-b border-gray-100 px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-gray-900">
                            {p.code}
                          </span>
                          <span className="text-xs text-gray-700">
                            {p.label}
                          </span>
                        </div>
                        {p.description && (
                          <p className="mt-0.5 truncate text-xs text-gray-500">
                            {p.description}
                          </p>
                        )}
                      </div>
                      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          onClick={() => openEdit(p.code)}
                          aria-label="수정"
                        >
                          <HugeiconsIcon icon={Edit02Icon} size={12} />
                        </Button>
                        <Button
                          size="icon-xs"
                          variant="ghost"
                          onClick={() => setPendingDelete(p)}
                          aria-label="삭제"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <HugeiconsIcon icon={Delete02Icon} size={12} />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
      >
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>권한 비활성화</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && (
                <>
                  <span className="mb-1 block font-mono text-xs text-gray-900">
                    {pendingDelete.code}
                  </span>
                  <span className="text-xs text-gray-700">
                    이 권한이 부여된 모든 직원에서 즉시 회수됩니다. 매핑은 보존되어 추후 복구 가능합니다.
                  </span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={toggleMutation.isPending}
              onClick={() => {
                if (!pendingDelete) return;
                toggleMutation.mutate(
                  { code: pendingDelete.code, isActive: false },
                  { onSuccess: () => setPendingDelete(null) },
                );
              }}
            >
              {toggleMutation.isPending ? "처리 중..." : "비활성화"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
